/**
 * Aligned with meeteor-web `lib/tableEventUi.ts` + `tableEventDays.ts` (public table-reservation path).
 * Widget has `event` and `venue` from GET /api/v1/widget/:id.
 */

import type { EventData, VenueData } from '../types';
import { getEventDayKeys } from './eventDayKeys';

export function getEventSlugForWebTables(event: EventData | null | undefined): string | null {
  if (!event?.slug?.trim()) return null;
  return event.slug.trim();
}

function isLoungeOrStandardKind(v: string | undefined | null): boolean {
  if (!v) return false;
  const s = String(v).toLowerCase();
  return s === 'lounge' || s === 'standard';
}

/**
 * Same signals as meeteor-web `isLoungeOrStandardSeatedVenue` (`type`, `stage_type`, `venue_type`).
 * Widget GET /api/v1/widget/:id often omits `venue.type` — see `isWidgetTableReservationOnly` fallback.
 */
export function isLoungeOrStandardVenue(
  venue: VenueData | null | undefined,
  event?: EventData | null | undefined,
): boolean {
  if (venue) {
    if (isLoungeOrStandardKind(venue.type)) return true;
    if (isLoungeOrStandardKind(venue.stage_type)) return true;
  }
  if (event && isLoungeOrStandardKind(event.venue_type)) return true;
  return false;
}

function hasWidgetTableMap(venue: VenueData | null | undefined): boolean {
  return Array.isArray(venue?.tables) && venue.tables.length > 0;
}

/** Seated + slug + lounge/standard venue (or table map when API omits venue kind). */
export function isWidgetTableReservationOnly(
  event: EventData | null | undefined,
  venue: VenueData | null | undefined,
): boolean {
  if (!event) return false;
  if (event.seated_event !== true) return false;
  if (!getEventSlugForWebTables(event)) return false;
  if (isLoungeOrStandardVenue(venue, event)) return true;
  /** Many widget payloads omit `venue.type`; if the event is seated and already has table rows, use table flow. */
  return hasWidgetTableMap(venue);
}

const DAY_ALIASES: Record<string, string> = {
  sun: 'sun',
  sunday: 'sun',
  mon: 'mon',
  monday: 'mon',
  tue: 'tue',
  tues: 'tue',
  tuesday: 'tue',
  wed: 'wed',
  wednesday: 'wed',
  thu: 'thu',
  thur: 'thu',
  thurs: 'thu',
  thursday: 'thu',
  fri: 'fri',
  friday: 'fri',
  sat: 'sat',
  saturday: 'sat',
};

export function normalizeDayKey(d: string | undefined | null): string {
  if (!d) return '';
  const s = String(d).trim().toLowerCase();
  if (DAY_ALIASES[s]) return DAY_ALIASES[s];
  if (s.length <= 3) return s.slice(0, 3);
  return DAY_ALIASES[s] || s.slice(0, 3);
}

/**
 * Table booking day toggles: same rules as the meetor app `getEventDayKeys` (scheduled
 * occurrence day(s) from `event_date_breakdown` or `event_start_date` — not a start→end
 * calendar walk that adds the next day for overnight end times).
 */
export function eventWeekdayKeysFromEvent(event: EventData): string[] {
  return getEventDayKeys(event as any);
}

export function shortDayLabel(key: string): string {
  const map: Record<string, string> = {
    sun: 'Sun',
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
  };
  return map[key] || key.slice(0, 3).replace(/^\w/, (c) => c.toUpperCase());
}

export function tableSlotKey(
  tableId: string,
  slot: { day: string; start_time: string; end_time: string } | null,
): string {
  if (!slot) return tableId;
  return `${tableId}-${normalizeDayKey(slot.day)}-${slot.start_time}-${slot.end_time}`;
}
